import React, { useState, useEffect } from "react";
import { useRefinementList } from "react-instantsearch";
import { CgSearch } from "react-icons/cg";

// Custom hook to get categories with the InstantSearch refinement list
const useCategoryRefinementList = (searchClient) => {
  const { items, refine } = useRefinementList({
    attribute: "category",
    limit: 1000,
    sortBy: ["name:asc"],
  });

  return { items, refine };
};

const CategoryFilterModal = ({ isOpen, onClose, searchClient }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const { items, refine } = useCategoryRefinementList(searchClient);

  // Get currently selected categories from instantsearch
  useEffect(() => {
    if (items) {
      const selected = items
        .filter((item) => item.isRefined)
        .map((item) => item.value);
      setSelectedCategories(selected);
    }
  }, [items]);

  // Handle category selection
  const handleCategorySelect = (value) => {
    refine(value);
  };

  // Clear all filters
  const clearFilters = () => {
    selectedCategories.forEach((category) => {
      refine(category);
    });
    onClose();
  };

  // Filter categories based on search query
  const filteredCategories = items
    ? items.filter((item) =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-50 z-[200]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Side drawer */}
      <div
        className="fixed top-0 pb-50 right-0 w-[260px] max-w-[85vw] h-full bg-pl-bg z-[201] shadow-lg border-l border-pl-border flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-10 pr-20 bg-pl-bg-elevated border-y border-pl-border">
          <h2
            id="modal-headline"
            className="text-16 font-medium text-pl-text-secondary"
          >
            Filters
          </h2>
          <button
            className="text-12 px-15 py-0.5 rounded-25 bg-pl-accent text-white border border-pl-accent"
            onClick={onClose}
          >
            Save
          </button>
        </div>

        {/* Search input */}
        <div className="px-4 py-3 border-b border-pl-border bg-pl-bg-elevated">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search categories..."
              className="w-full h-[1.8rem] pl-8 pr-3 rounded-full border-0 bg-[#dcdcdc83] text-13 text-pl-text-primary placeholder-pl-text-tertiary focus:outline-none"
            />
            <CgSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-pl-text-tertiary w-4 h-4" />
          </div>
        </div>

        {/* Category list */}
        <div className="flex-grow overflow-y-auto px-4 py-2">
          {filteredCategories.length > 0 ? (
            <div className="flex flex-col">
              {filteredCategories.map((category) => (
                <div key={category.value} className="border-b border-pl-border last:border-b-0">
                  <label className="flex items-center py-2 cursor-pointer text-pl-text-tertiary">
                    <div className="relative ml-5 mr-2 flex h-[12px] w-[12px] flex-shrink-0 items-center justify-center">
                      <input
                        type="checkbox"
                        checked={category.isRefined}
                        onChange={() => handleCategorySelect(category.value)}
                        className="appearance-none h-[12px] w-[12px] border rounded-[2px] border-[#ccc] checked:border-pl-accent focus:outline-none"
                      />
                      {category.isRefined && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="h-[12px] w-[12px] bg-pl-accent rounded-[2px]">
                            <svg
                              className="h-[9px] w-[9px] text-white"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                    <span className="block text-13 text-pl-text-tertiary whitespace-normal break-words">
                      {category.label}
                    </span>
                    <span className="ml-auto rounded-full bg-[#9d9d9d30] px-2 text-10 font-normal text-pl-text-secondary dark:bg-[#434343]">
                      {category.count}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center h-40 text-pl-text-tertiary text-14">
              No categories found matching your search.
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-4 py-3 border-t border-pl-border flex justify-end">
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium rounded-pl border border-pl-border text-pl-text-tertiary hover:bg-pl-bg-subtle transition-colors"
          >
            Clear
          </button>
        </div>
      </div>
    </>
  );
};

export default CategoryFilterModal;
